import { Box, Card, Typography, CircularProgress } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

import { useCelleraTheme } from "theme/useCelleraTheme";

export interface WellBeingScore {
  date: string;
  value: number;
  formattedDate: string;
}

interface DailyWellBeingScoreChartProps {
  data: WellBeingScore[];
  isLoading: boolean;
}

interface WellBeingTooltipPayload {
  payload: WellBeingScore;
  value: number;
  dataKey: string;
}

const DailyWellBeingTooltip: React.FC<{
  active?: boolean;
  payload?: WellBeingTooltipPayload[];
}> = ({ active, payload }) => {
  const { colors } = useCelleraTheme();

  if (active && payload?.length) {
    const tooltipData = payload[0].payload;
    return (
      <Box
        sx={{
          backgroundColor: "white",
          border: `1px solid ${colors.border.light}`,
          borderRadius: 2,
          p: 2,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          {tooltipData.formattedDate}
        </Typography>
        <Typography variant="body2" sx={{ color: colors.primary.main }}>
          Score: {tooltipData.value}
        </Typography>
      </Box>
    );
  }

  return null;
};

const DailyWellBeingScoreChart: React.FC<DailyWellBeingScoreChartProps> = ({
  data,
  isLoading,
}) => {
  const { colors } = useCelleraTheme();
  const [ticks, setTicks] = useState<number[]>([]);

  const calculateTicks = useCallback((maxValue: number) => {
    const result = [];

    if (maxValue > 50) {
      let current = 0;
      while (current <= maxValue) {
        result.push(current);
        current += 10;
      }
    } else if (maxValue <= 50 && maxValue > 30) {
      let current = 0;
      while (current <= maxValue) {
        result.push(current);
        current += 5;
      }
    } else if (maxValue <= 30 && maxValue > 10) {
      let current = 0;
      while (current <= maxValue) {
        result.push(current);
        current += 2;
      }
    } else if (maxValue <= 10) {
      for (let i = 1; i <= 10; i++) {
        result.push(i);
      }
    }

    return result;
  }, []);

  const calculateInterval = useCallback((length: number) => {
    if (length >= 28) {
      return 6;
    }
    if (length > 14) {
      return 2;
    }
    if (length > 7) {
      return 1;
    }
    return 0;
  }, []);

  useEffect(() => {
    if (data.length === 0) {
      setTicks([]);
      return;
    }
    const maxValue = Math.max(...data.map((item) => item.value));
    setTicks(calculateTicks(maxValue));
  }, [data, calculateTicks]);

  if (isLoading) {
    return (
      <Card
        sx={{
          border: `2px solid ${colors.border.main}`,
          borderRadius: 2,
          height: "100%",
          p: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            gap: 2,
          }}
        >
          <CircularProgress size={40} sx={{ color: colors.primary.main }} />
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            Loading wellbeing score data...
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: "calc(100% - 70px)",
        display: "flex",
        border: "none",
        boxShadow: "none",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flex: 1, width: "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
          {data.length > 0 ? (
            <BarChart
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid
                stroke={colors.border.main}
                strokeWidth={1}
                horizontal
                vertical
              />
              <XAxis
                dataKey="formattedDate"
                axisLine
                tick={{ fontSize: 12, fill: colors.text.secondary }}
                interval={calculateInterval(data.length)}
              />
              <YAxis
                domain={[0, ticks.length > 0 ? Math.max(...ticks) : "auto"]}
                axisLine
                tick={{ fontSize: 12, fill: colors.text.secondary }}
                tickCount={6}
                ticks={ticks}
                tickFormatter={(value) => value.toString()}
              />
              <Tooltip content={<DailyWellBeingTooltip />} />
              <Bar
                dataKey="value"
                fill={colors.primary.main}
                radius={[2, 2, 0, 0]}
                maxBarSize={40}
              />
            </BarChart>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: colors.text.secondary, fontSize: 18 }}
              >
                No data available
              </Typography>
            </Box>
          )}
        </ResponsiveContainer>
      </Box>
    </Card>
  );
};

export default DailyWellBeingScoreChart;
